import { API_ROUTES } from '@/api/apiRoutes';
import { log } from '@/logger/logger';
import { httpReq } from '@/utils/api.utils';
import { FolderScope } from '@/enums/folder-scope.enum';
import { safeAxios } from '@/services/safeAxios';
import type { ArchiveItemsResponse } from './dashboard.api.types';
import type { FolderResponseDto, ListFolderResponseDto, CreateFolderRequestDto } from '@/types/folder.types';

const logger = log.getLogger('folders.api');

export const listFolderContents = async (scope: FolderScope): Promise<ListFolderResponseDto> => {
  // Always fetch all folders and recipes, no parentId parameter
  try {
    const res = await safeAxios.get<ListFolderResponseDto>(API_ROUTES.folders.list(), {
      params: { scope },
    });

    logger.debug(`${scope} folder contents response:`, {
      folderCount: res.data.folders.length,
      recipeCount: res.data.recipes.length,
      folders: res.data.folders.map((f) => ({ id: f.id, name: f.name, parentId: f.parentId })),
      recipes: res.data.recipes.map((r) => ({ id: r.id, name: r.name, folderId: r.folderId })),
      allRecipeFolderIds: [...new Set(res.data.recipes.map((r) => r.folderId))],
      allFolderParentIds: [...new Set(res.data.folders.map((f) => f.parentId))],
    });

    return res.data;
  } catch (e) {
    logger.error(`Error fetching ${scope} folder contents:`, e);
    throw e;
  }
};

export const createFolder = async (
  name: string,
  scope: FolderScope,
  workspaceId: string,
  parentId?: string | null,
): Promise<FolderResponseDto> => {
  try {
    const req: CreateFolderRequestDto = {
      name,
      workspaceId,
      scope,
      ...(parentId && { parentId }),
    };

    return await httpReq<CreateFolderRequestDto, FolderResponseDto>(API_ROUTES.folders.root(), req);
  } catch (e) {
    logger.error('Error creating folder:', e);
    throw e;
  }
};

export const renameFolder = async (id: string, name: string): Promise<void> => {
  try {
    await safeAxios.patch(API_ROUTES.folders.byId({ folderId: id }), { name });
  } catch (e) {
    logger.error('Error renaming folder:', e);
    throw e;
  }
};

export const moveFolder = async (id: string, parentId: string | null, scope: FolderScope): Promise<void> => {
  try {
    await safeAxios.patch(API_ROUTES.folders.byId({ folderId: id }), { parentId, scope });
  } catch (e) {
    logger.error('Error moving folder:', e);
    throw e;
  }
};

export const deleteFolder = async (id: string): Promise<void> => {
  try {
    await safeAxios.delete(API_ROUTES.folders.byId({ folderId: id }));
  } catch (e) {
    logger.error('Error deleting folder:', e);
    throw e;
  }
};

export const moveRecipe = async (id: string, folderId: string | null, scope: FolderScope): Promise<void> => {
  try {
    await httpReq<{ folderId: string | null; scope: FolderScope }, void>(API_ROUTES.recipes.move({ recipeId: id }), {
      folderId,
      scope,
    });
  } catch (e) {
    logger.error('Error moving recipe:', e);
    throw e;
  }
};

export const getDeletedFolders = async (): Promise<ArchiveItemsResponse> => {
  try {
    const res = await safeAxios.get<ArchiveItemsResponse>(API_ROUTES.folders.deleted());
    return res.data;
  } catch (e) {
    logger.error('Error getting deleted folders:', e);
    throw e;
  }
};

export const restoreFolders = async (folderIds: string[]): Promise<void> => {
  try {
    await safeAxios.patch(API_ROUTES.folders.restore(), { folderIds });
  } catch (e) {
    logger.error('Error restoring folders:', e);
    throw e;
  }
};
